import {
  GuestSessionSchema,
  RegistryResponseSchema,
  type GuestSession,
  type RegistryResponse,
} from '@resto/contracts';

export const API_BASE = (import.meta.env.VITE_API_BASE_URL ?? '/api/v1').replace(/\/+$/, '');

/** Абсолютный адрес API — виджет живёт в iframe на другом origin. */
export function apiBaseForWidget(): string {
  const absolute = new URL(API_BASE.endsWith('/') ? API_BASE : `${API_BASE}/`, window.location.origin);
  return absolute.toString().replace(/\/+$/, '');
}

export class HostApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'HostApiError';
    this.status = status;
    this.code = code;
  }
}

async function requestJson(path: string, init: RequestInit = {}, token?: string): Promise<unknown> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;

  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, { ...init, headers });
  } catch {
    throw new HostApiError(
      0,
      'network_error',
      `API недоступен по адресу ${API_BASE}. Проверьте, что ядро запущено.`,
    );
  }

  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { code?: string; message?: string };
    throw new HostApiError(
      response.status,
      body.code ?? 'http_error',
      body.message ?? `API ответил ${response.status}`,
    );
  }

  return response.json();
}

export async function createGuestSession(
  venueId: string,
  channel: string,
  widgetId?: string,
): Promise<GuestSession> {
  const raw = await requestJson('/guest/session', {
    method: 'POST',
    body: JSON.stringify({ venueId, channel, locale: 'ru', ...(widgetId ? { widgetId } : {}) }),
  });
  return GuestSessionSchema.parse(raw);
}

export async function refreshGuestSession(refreshToken: string): Promise<GuestSession> {
  const raw = await requestJson('/guest/refresh', {
    method: 'POST',
    body: JSON.stringify({ refreshToken }),
  });
  return GuestSessionSchema.parse(raw);
}

export async function loadRegistry(
  token: string,
  channel: string,
  hostVersion: string,
): Promise<RegistryResponse> {
  const query = new URLSearchParams({ channel, hostVersion }).toString();
  const raw = await requestJson(`/registry?${query}`, {}, token);
  return RegistryResponseSchema.parse(raw);
}

export function describeApiError(error: unknown): { message: string; hint: string } {
  if (error instanceof HostApiError) {
    if (error.status === 0) {
      return {
        message: error.message,
        hint: 'Запустите ядро: сервис FastAPI на http://localhost:8000 (см. docs/api-m0.md).',
      };
    }
    if (error.status === 401 || error.status === 403) {
      return {
        message: `API отклонил гостевую сессию (${error.code}).`,
        hint: 'Перезагрузите страницу — хост создаст нового гостя.',
      };
    }
    if (error.status >= 500) {
      return {
        message: `${error.message} (HTTP ${error.status})`,
        hint: `Ядро недоступно или отвечает ошибкой. Проверьте сервис на ${
          import.meta.env.VITE_API_PROXY_TARGET ?? 'http://localhost:8000'
        } и логи прокси Vite.`,
      };
    }
    return {
      message: `${error.message} (HTTP ${error.status}${error.code ? `, ${error.code}` : ''})`,
      hint: 'Смотрите логи ядра и повторите попытку.',
    };
  }
  if (error instanceof Error) {
    return {
      message: error.message,
      hint: 'Ответ API не совпал с контрактом @resto/contracts — смотрите детали ниже.',
    };
  }
  return { message: String(error), hint: 'Неизвестная ошибка загрузки.' };
}


export interface WidgetEventInput {
  widgetId: string;
  eventName:
    | 'widget.open'
    | 'widget.close'
    | 'menu.view'
    | 'item.view'
    | 'cart.add'
    | 'checkout.start'
    | 'order.created'
    | 'payment.start'
    | 'payment.success'
    | 'widget.error'
    | 'widget.navigate';
  payload?: Record<string, unknown>;
  version?: string;
}

/** Телеметрия виджета: не блокирует сценарий и не роняет хост при ошибке сети. */
export function sendWidgetEvent(token: string, event: WidgetEventInput): void {
  void fetch(`${API_BASE}/events`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(event),
  }).catch(() => undefined);
}
